<template>
  <div class="py-4 container-fluid wo-page">
    <!-- ======= HERO ======= -->
    <div class="hero mb-4">
      <div class="hero-head">
        <div>
          <span class="hero-kicker"><i class="fas fa-handshake"></i> Sales · Work Order</span>
          <h3 class="hero-title">Work Order</h3>
          <span class="hero-sub">Daftar WO dari Sales, dipakai sebagai referensi nomor WO saat membuat Purchase Request</span>
        </div>
        <div class="hero-actions">
          <a class="hero-btn" :href="exportUrl('export-excel/daily-activity')" target="_BLANK" title="Export Excel"><i class="fas fa-file-excel"></i></a>
          <a class="hero-btn" :href="exportUrl('print-pdf/daily-activity')" target="_BLANK" title="Export PDF"><i class="fas fa-file-pdf"></i></a>
          <button type="button" class="hero-btn hero-btn-main" @click="create()"><i class="fas fa-plus"></i> WO Baru</button>
        </div>
      </div>

      <div class="hero-body">
        <!-- donut jatuh tempo -->
        <div class="donut-wrap">
          <svg viewBox="0 0 120 120" class="donut">
            <circle cx="60" cy="60" r="48" class="donut-track" />
            <circle v-for="seg in donutSegments" :key="seg.key" cx="60" cy="60" r="48" class="donut-seg"
              :stroke="seg.color" :stroke-dasharray="seg.dash" :stroke-dashoffset="seg.offset" />
          </svg>
          <div class="donut-center">
            <span class="donut-num">{{ s.total || 0 }}</span>
            <span class="donut-lbl">Work Order</span>
          </div>
        </div>
        <div class="donut-legend">
          <div v-for="seg in donutSegments" :key="seg.key" class="legend-row" :class="{ active: search.due == seg.key }" @click="setDue(search.due == seg.key ? '' : seg.key)">
            <span class="legend-dot" :style="{ background: seg.color }"></span>
            <span class="legend-name">{{ seg.label }}</span>
            <span class="legend-val">{{ seg.value }}</span>
          </div>
          <span class="legend-hint">Klik untuk memfilter</span>
        </div>

        <!-- angka utama -->
        <div class="hero-metrics">
          <div class="metric">
            <span class="metric-lbl"><i class="fas fa-calendar-plus"></i> WO Tahun {{ currentYear }}</span>
            <span class="metric-val">{{ s.this_year || 0 }} <small>/ {{ s.total || 0 }} total</small></span>
          </div>
          <div class="metric">
            <span class="metric-lbl"><i class="fas fa-layer-group"></i> Tipe WO</span>
            <div class="type-mini">
              <span v-for="t in typeList" :key="t.code" :class="'tm-' + t.code" @click="setType(search.project_type == t.code ? '' : t.code)">
                <b>{{ stats.types[t.code] || 0 }}</b> {{ t.label }}
              </span>
            </div>
          </div>
          <div class="metric">
            <span class="metric-lbl"><i class="fas fa-hourglass-half"></i> Rata-rata Durasi</span>
            <span class="metric-val">{{ s.avg_duration !== null && s.avg_duration !== undefined ? s.avg_duration + ' hari' : '-' }}</span>
            <span class="metric-note">dari C Date sampai Due Date</span>
          </div>
        </div>

        <!-- client terbanyak -->
        <div class="podium">
          <span class="podium-title">Client Terbanyak · {{ s.clients || 0 }} client</span>
          <div v-for="(count, name, i) in stats.clients" :key="name" class="podium-row" @click="searchText(name)" :title="'Cari WO ' + name">
            <span class="podium-rank">{{ i + 1 }}</span>
            <div class="podium-info">
              <span class="podium-code">{{ name }}</span>
              <div class="podium-bar"><div :style="{ width: (count / maxClient * 100) + '%' }"></div></div>
            </div>
            <span class="podium-val">{{ count }} WO</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ======= TABEL ======= -->
    <div class="card">
      <div class="card-header pb-2">
        <div class="row align-items-center">
          <div class="col-xl-4 col-lg-12 mb-2">
            <div class="input-group">
              <span class="input-group-text"><i class="fa fa-search"></i></span>
              <input type="text" class="form-control" placeholder="Cari no. WO, project, client, PO client, site..." v-model="search.q" @input="onSearch()">
            </div>
          </div>
          <div class="col-xl-8 col-lg-12 mb-2 d-flex flex-wrap align-items-center justify-content-xl-end gap-2">
            <div class="btn-group btn-group-sm mb-0">
              <button type="button" class="btn mb-0" :class="!search.project_type ? 'btn-dark' : 'btn-outline-dark'" @click="setType('')">Semua Tipe</button>
              <button v-for="t in typeList" :key="t.code" type="button" class="btn mb-0" :class="search.project_type == t.code ? 'btn-dark' : 'btn-outline-dark'" @click="setType(t.code)">{{ t.code }} · {{ stats.types[t.code] || 0 }}</button>
            </div>
            <div class="btn-group btn-group-sm mb-0">
              <button v-for="d in dueTabs" :key="d.value" type="button" class="btn mb-0" :class="search.due == d.value ? 'btn-primary' : 'btn-outline-primary'" @click="setDue(d.value)">{{ d.label }}</button>
            </div>
            <argon-button variant="gradient" color="secondary" size="sm" @click="filter()"><i class="fa fa-filter fa-sm"></i> Filter Lanjutan</argon-button>
          </div>
        </div>
        <div v-if="activeChips.length" class="mt-1">
          <span v-for="chip in activeChips" :key="chip.key" class="badge bg-light text-dark me-2 filter-chip" @click="clearFilter(chip.key)">{{ chip.label }} <i class="fa fa-times ms-1"></i></span>
          <span class="text-xs text-primary filter-chip" @click="resetFilter()">Hapus semua filter</span>
        </div>
      </div>

      <div class="card-body px-0 pt-0 pb-2">
        <div class="table-responsive p-0 scroll">
          <table class="table table-hover table-striped align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-center text-uppercase text-white text-xxs font-weight-bolder">No</th>
                <th class="text-center text-uppercase text-white text-xxs font-weight-bolder">WO No</th>
                <th class="text-center text-uppercase text-white text-xxs font-weight-bolder">PO Number</th>
                <th class="text-center text-uppercase text-white text-xxs font-weight-bolder">Nilai PO</th>
                <th class="text-center text-uppercase text-white text-xxs font-weight-bolder">C Date</th>
                <th class="text-center text-uppercase text-white text-xxs font-weight-bolder">Client</th>
                <th class="text-center text-uppercase text-white text-xxs font-weight-bolder">Owner</th>
                <th class="text-center text-uppercase text-white text-xxs font-weight-bolder">Project</th>
                <th class="text-center text-uppercase text-white text-xxs font-weight-bolder">Site</th>
                <th class="text-center text-uppercase text-white text-xxs font-weight-bolder">Kinds</th>
                <th class="text-center text-uppercase text-white text-xxs font-weight-bolder">Unit</th>
                <th class="text-center text-uppercase text-white text-xxs font-weight-bolder">Qty</th>
                <th class="text-center text-uppercase text-white text-xxs font-weight-bolder">Due Date</th>
                <th class="text-center text-uppercase text-white text-xxs font-weight-bolder">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading"><td colspan="14" class="text-center text-sm text-secondary py-5"><i class="fas fa-spinner fa-spin"></i> Memuat data...</td></tr>
              <tr v-else-if="!table.data.length"><td colspan="14" class="text-center text-sm text-secondary py-5">Tidak ada Work Order yang cocok dengan filter</td></tr>
              <tr v-for="(row, i) in (loading ? [] : table.data)" :key="row.id">
                <td class="align-middle text-center"><span class="text-secondary text-xs font-weight-bold">{{ (pagination.page - 1) * perPage + i + 1 }}</span></td>
                <td class="align-middle text-center"><span class="text-xs font-weight-bolder wo-link" @click="openDetail(row)">{{ row.wo_code }}</span></td>
                <td class="align-middle text-left"><span class="text-secondary text-xs font-weight-bold">{{ row.po_number }}</span></td>
                <td class="align-middle text-end"><span class="text-xs font-weight-bold" :class="row.po_value ? 'text-dark' : 'text-secondary'">{{ poValueText(row) }}</span></td>
                <td class="align-middle text-center"><span class="text-secondary text-xs font-weight-bold">{{ fmtDate(row.c_date) }}</span></td>
                <td class="align-middle text-left"><span class="text-secondary text-xs font-weight-bold">{{ row.client }}</span></td>
                <td class="align-middle text-left"><span class="text-secondary text-xs font-weight-bold">{{ row.owner }}</span></td>
                <td class="align-middle text-left"><span class="text-secondary text-xs font-weight-bold">{{ row.project }}</span></td>
                <td class="align-middle text-left"><span class="text-secondary text-xs font-weight-bold">{{ row.site }}</span></td>
                <td class="align-middle text-left"><span class="text-secondary text-xs font-weight-bold">{{ row.kinds }}</span></td>
                <td class="align-middle text-center"><span class="text-secondary text-xs font-weight-bold">{{ row.weight }}</span></td>
                <td class="align-middle text-center"><span class="text-secondary text-xs font-weight-bold">{{ row.qty ? Number(row.qty).toLocaleString('id-ID') : '-' }}</span></td>
                <td class="align-middle text-center">
                  <span class="text-secondary text-xs font-weight-bold d-block">{{ fmtDate(row.due_date) }}</span>
                  <span class="due-badge" :class="'due-' + dueOf(row).level">{{ dueOf(row).label }}</span>
                </td>
                <td class="align-middle text-center text-nowrap">
                  <i class="fas fa-eye action-icon text-info" title="Lihat detail" @click="openDetail(row)"></i>
                  <i class="fas fa-edit action-icon" title="Edit data ini" @click="edit(row.id)"></i>
                  <i class="fa fa-trash action-icon" title="Hapus data ini" @click="remove(row)"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="card-footer d-flex justify-content-between align-items-center">
        <span class="text-xs text-secondary">{{ pagination.total }} Work Order</span>
        <base-pagination :page-count="pagination.page_count" :value="pagination.page" @input="changePage" :total="pagination.page_count" class="justify-content-end mb-0"></base-pagination>
      </div>
    </div>
  </div>

  <!-- =======  MODAL DETAIL ======= -->
  <div class="container">
    <vue-final-modal v-model="detail.show" classes="modal-container" content-class="modal-content modal-detail" :z-index="10000">
      <template v-if="detail.row">
        <div class="d-flex justify-content-between align-items-start">
          <div class="d-flex align-items-center">
            <div class="avatar-initial avatar-lg" :class="'type-bg-' + detail.row.project_type">{{ detail.row.project_type }}</div>
            <div class="ms-3">
              <span class="modal__title d-block">{{ detail.row.wo_code }}</span>
              <span class="text-sm text-dark d-block">{{ has(detail.row.project) ? detail.row.project : typeName(detail.row.project_type) }}</span>
              <span class="info-chip mt-1">{{ typeName(detail.row.project_type) }}</span>
              <span class="due-badge" :class="'due-' + dueOf(detail.row).level">{{ dueOf(detail.row).label }}</span>
            </div>
          </div>
          <i style="cursor: pointer;" class="fa fa-times" @click="detail.show = false"></i>
        </div>
        <hr class="my-3">

        <div class="info-grid">
          <div class="info-cell"><i class="far fa-building"></i><div><span class="info-lbl">Client</span><span class="info-val">{{ detail.row.client }}</span></div></div>
          <div class="info-cell"><i class="fas fa-user-tie"></i><div><span class="info-lbl">Owner</span><span class="info-val">{{ has(detail.row.owner) ? detail.row.owner : '-' }}</span></div></div>
          <div class="info-cell"><i class="fas fa-file-contract"></i><div><span class="info-lbl">PO Client</span><span class="info-val">{{ has(detail.row.po_number) ? detail.row.po_number : '-' }}</span></div></div>
          <div class="info-cell"><i class="fas fa-map-marker-alt"></i><div><span class="info-lbl">Site</span><span class="info-val">{{ has(detail.row.site) ? detail.row.site : '-' }}</span></div></div>
          <div class="info-cell"><i class="fas fa-cubes"></i><div><span class="info-lbl">Kinds</span><span class="info-val">{{ has(detail.row.kinds) ? detail.row.kinds : '-' }}</span></div></div>
          <div class="info-cell"><i class="fas fa-money-bill-wave"></i><div><span class="info-lbl">Nilai PO</span><span class="info-val">{{ poValueText(detail.row) }}</span></div></div>
          <div class="info-cell"><i class="fas fa-balance-scale"></i><div><span class="info-lbl">Qty</span><span class="info-val">{{ qtyText(detail.row) }}</span></div></div>
        </div>

        <div class="schedule-box mt-3">
          <div class="d-flex justify-content-between align-items-end">
            <div><span class="info-lbl">C Date</span><span class="sched-date">{{ fmtDate(detail.row.c_date) }}</span></div>
            <div class="text-center text-xs" :class="duration(detail.row) < 0 ? 'text-danger' : 'text-secondary'">{{ durationLabel(detail.row) }}</div>
            <div class="text-end"><span class="info-lbl">Due Date</span><span class="sched-date">{{ fmtDate(detail.row.due_date) }}</span></div>
          </div>
          <div class="time-bar time-bar-lg"><div :class="{ over: dueOf(detail.row).level == 'over' }" :style="{ width: timeProgress(detail.row) + '%' }"></div></div>
          <span class="text-xs text-secondary">{{ scheduleNote(detail.row) }}</span>
        </div>

        <div class="audit mt-3">
          <span><i class="fas fa-plus-circle"></i> Dibuat {{ detail.row.created_by || '-' }} · {{ fmtDateTime(detail.row.created_at) }}</span>
          <span v-if="detail.row.updated_by"><i class="fas fa-pen"></i> Diubah {{ detail.row.updated_by }} · {{ fmtDateTime(detail.row.updated_at) }}</span>
        </div>

        <div class="d-flex justify-content-end mt-3">
          <argon-button variant="gradient" color="secondary" size="sm" class="me-2" @click="detail.show = false">Tutup</argon-button>
          <argon-button variant="gradient" color="info" size="sm" @click="editFromDetail()"><i class="fas fa-edit"></i> Edit WO</argon-button>
        </div>
      </template>
    </vue-final-modal>
  </div>

  <!-- =======  MODAL ADD / EDIT ======= -->
  <div class="container">
    <vue-final-modal v-model="form.show" classes="modal-container" content-class="modal-content modal-form" :z-index="10000">
      <div class="d-flex justify-content-between">
        <span class="modal__title">{{ form.title == 'Add Data' ? 'Tambah Work Order' : 'Edit Work Order ' + (workOrderData.wo_code || '') }}</span>
        <i style="cursor: pointer;" class="fa fa-times" @click="form.show = false"></i>
      </div>
      <hr class="my-2">
      <div class="modal__content">
        <div class="row">
          <div class="col-md-6">
            <p class="section-title mt-2">Identitas</p>
            <template v-if="form.title == 'Add Data'">
              <label class="form-control-label">Project Type <span class="text-danger">*</span></label>
              <select class="form-select" v-model="workOrderData.project_type">
                <option v-for="t in typeList" :key="t.code" :value="t.code">{{ t.code }} - {{ t.label }}</option>
              </select>
              <span class="text-xxs text-secondary">No. WO dibuat otomatis dari tipe + tahun</span>
            </template>
            <label class="form-control-label mt-2">Project</label>
            <input type="text" class="form-control" placeholder="Nama project" v-model="workOrderData.project">
            <label class="form-control-label mt-2">PO Number (Client) <span class="text-danger">*</span></label>
            <input type="text" class="form-control" placeholder="PO Number" v-model="workOrderData.po_number">
            <label class="form-control-label mt-2">Nilai PO <span class="text-xxs text-secondary">(opsional)</span></label>
            <div class="input-group">
              <select class="form-select po-currency" v-model="workOrderData.po_currency">
                <option v-for="c in currencies" :key="c" :value="c">{{ c }}</option>
              </select>
              <input type="number" min="0" step="any" class="form-control" placeholder="Boleh dikosongkan" v-model="workOrderData.po_value">
            </div>
            <span class="text-xxs text-secondary d-block" v-if="workOrderData.po_value">{{ money(workOrderData.po_value, workOrderData.po_currency) }}</span>
            <label class="form-control-label mt-2">Client <span class="text-danger">*</span></label>
            <input type="text" class="form-control" placeholder="Client" v-model="workOrderData.client">
            <label class="form-control-label mt-2">Owner <span class="text-danger">*</span></label>
            <input type="text" class="form-control" placeholder="Owner" v-model="workOrderData.owner">
          </div>
          <div class="col-md-6">
            <p class="section-title mt-2">Lingkup & Jadwal</p>
            <label class="form-control-label">Site <span class="text-danger">*</span></label>
            <input type="text" class="form-control" placeholder="Site" v-model="workOrderData.site">
            <label class="form-control-label mt-2">Kinds <span class="text-danger">*</span></label>
            <input type="text" class="form-control" placeholder="Jenis pekerjaan" v-model="workOrderData.kinds">
            <div class="row">
              <div class="col-6">
                <label class="form-control-label mt-2">Qty <span class="text-danger">*</span></label>
                <input type="number" class="form-control" placeholder="Qty" v-model="workOrderData.qty">
              </div>
              <div class="col-6">
                <label class="form-control-label mt-2">Unit <span class="text-danger">*</span></label>
                <select class="form-select" v-model="workOrderData.weight">
                  <option v-for="(u, k) in tableUom.data" :key="k" :value="u.code">{{ u.name }}</option>
                </select>
              </div>
            </div>
            <div class="row">
              <div class="col-6">
                <label class="form-control-label mt-2">C Date <span class="text-danger">*</span></label>
                <input type="date" class="form-control" v-model="workOrderData.c_date">
              </div>
              <div class="col-6">
                <label class="form-control-label mt-2">Due Date <span class="text-danger">*</span></label>
                <input type="date" class="form-control" v-model="workOrderData.due_date">
              </div>
            </div>
            <span class="text-xxs text-danger" v-if="workOrderData.c_date && workOrderData.due_date && workOrderData.due_date < workOrderData.c_date">Due Date lebih awal dari C Date</span>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-end mt-3">
        <argon-button variant="gradient" color="secondary" size="sm" class="me-2" @click="form.show = false">Close</argon-button>
        <argon-button variant="gradient" color="success" size="sm" :disabled="saving" @click="save()">{{ saving ? 'Menyimpan...' : 'Save' }}</argon-button>
      </div>
    </vue-final-modal>
  </div>

  <!-- =======  MODAL FILTER ======= -->
  <div class="container">
    <vue-final-modal v-model="formFilter.show" classes="modal-container" content-class="modal-content" :z-index="10000">
      <div class="d-flex justify-content-between">
        <span class="modal__title">Filter Lanjutan</span>
        <i style="cursor: pointer;" class="fa fa-times" @click="formFilter.show = false"></i>
      </div>
      <hr class="my-2">
      <div class="modal__content">
        <label class="form-control-label">PO Number</label>
        <input type="text" class="form-control" placeholder="PO Number" v-model="search.po_number" v-on:keyup.enter="applyFilter()">
        <label class="form-control-label mt-2">Client</label>
        <input type="text" class="form-control" placeholder="Client" v-model="search.client" v-on:keyup.enter="applyFilter()">
        <label class="form-control-label mt-2">Owner</label>
        <input type="text" class="form-control" placeholder="Owner" v-model="search.owner" v-on:keyup.enter="applyFilter()">
        <label class="form-control-label mt-2">Site</label>
        <input type="text" class="form-control" placeholder="Site" v-model="search.site" v-on:keyup.enter="applyFilter()">
        <label class="form-control-label mt-2">Kinds</label>
        <input type="text" class="form-control" placeholder="Kinds" v-model="search.kinds" v-on:keyup.enter="applyFilter()">
        <div class="row">
          <div class="col-6">
            <label class="form-control-label mt-2">C Date</label>
            <input type="date" class="form-control" v-model="search.c_date">
          </div>
          <div class="col-6">
            <label class="form-control-label mt-2">Due Date</label>
            <input type="date" class="form-control" v-model="search.due_date">
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-end mt-3">
        <argon-button variant="gradient" color="secondary" size="sm" class="me-2" @click="resetFilter()">Clear</argon-button>
        <argon-button variant="gradient" color="success" size="sm" @click="applyFilter()">Filter</argon-button>
      </div>
    </vue-final-modal>
  </div>
</template>
<script>
import ArgonButton from "@/components/ArgonButton.vue";
import { VueFinalModal } from 'vue-final-modal'
import Api from '@/helpers/api';
import config from '@/configs/config';
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';
import BasePagination from "@/components/BasePagination";
var moment = require('moment');
import workOrder from '@/services/procurement/workOrder.service';
import unitOfMeasurement from '@/services/unitOfMeasurement.service';

const EMPTY_SEARCH = { q: '', due: '', project_type: '', po_number: '', c_date: '', client: '', owner: '', site: '', kinds: '', due_date: '' };
const DUE_SOON_DAYS = 14;   // sama dengan WorkOrderController::DUE_SOON_DAYS
const DONUT_CIRCUMFERENCE = 2 * Math.PI * 48;

export default {
  name: "WorkOrderList",
  components: { ArgonButton, VueFinalModal, BasePagination },
  data() {
    return {
      pagination: { page_count: 1, page: 1, total: 0 },
      table: { data: [] },
      tableUom: { data: [] },
      loading: false,
      saving: false,
      requestSeq: 0,
      searchTimer: null,
      todayStr: moment().format('YYYY-MM-DD'),
      currentYear: moment().year(),
      perPage: 100,   // sama dengan per_page di WorkOrderController::index
      currencies: ['IDR', 'USD', 'SGD', 'EUR', 'CNY'],
      stats: { summary: {}, types: {}, clients: {} },
      form: { title: "Add Data", show: false },
      formFilter: { show: false },
      detail: { show: false, row: null },
      workOrderData: {},
      search: { ...EMPTY_SEARCH },
      apiUrl: config.apiUrl,
      typeList: [
        { code: 'P', label: 'Project' },
        { code: 'I', label: 'Internal' },
        { code: 'M', label: 'Miscellaneous' },
      ],
      dueTabs: [
        { value: '', label: 'Semua' },
        { value: 'overdue', label: 'Lewat Due Date' },
        { value: 'soon', label: 'Due ≤ 14 Hari' },
        { value: 'later', label: 'Masih Lama' },
      ],
    };
  },
  computed: {
    s() {
      return this.stats.summary || {};
    },
    // donut status jatuh tempo: lewat / ≤ 14 hari / masih lama
    donutSegments() {
      const total = this.s.total || 0;
      const parts = [
        { key: 'overdue', label: 'Lewat Due Date', value: this.s.overdue || 0, color: '#f5365c' },
        { key: 'soon', label: 'Due ≤ 14 Hari', value: this.s.due_soon || 0, color: '#fb9b40' },
        { key: 'later', label: 'Masih Lama', value: this.s.later || 0, color: '#2dce89' },
      ];
      let offset = 0;
      return parts.map(p => {
        const len = total ? p.value / total * DONUT_CIRCUMFERENCE : 0;
        const seg = { ...p, dash: len + ' ' + DONUT_CIRCUMFERENCE, offset: -offset };
        offset += len;
        return seg;
      });
    },
    maxClient() {
      return Math.max(1, ...Object.values(this.stats.clients || {}));
    },
    activeChips() {
      const labels = { po_number: 'PO', c_date: 'C Date', client: 'Client', owner: 'Owner', site: 'Site', kinds: 'Kinds', due_date: 'Due Date' };
      return Object.keys(labels).filter(k => this.search[k]).map(k => ({ key: k, label: labels[k] + ': ' + this.search[k] }));
    },
  },
  mounted() {
    this.get();
    this.getStats();
    this.getUom();
  },
  methods: {
    getUom() {
      let context = this;
      Api(context, unitOfMeasurement.index()).onSuccess(function(response) {
        context.tableUom.data = response.data.data.data;
      }).onError(function() {
        context.tableUom.data = [];
      }).call();
    },
    getStats() {
      let context = this;
      Api(context, workOrder.stats()).onSuccess(function(response) {
        context.stats = response.data.data;
      }).call();
    },
    get() {
      let context = this;
      const seq = ++this.requestSeq;
      this.loading = true;
      Api(context, workOrder.index({ ...this.search, page: this.pagination.page })).onSuccess(function(response) {
        if (seq != context.requestSeq) return;
        const page = response.data.data.data;
        context.table.data = page.data;
        context.pagination.page_count = page.last_page;
        context.pagination.total = page.total;
      }).onError(function(error) {
        if (seq != context.requestSeq) return;
        context.table.data = [];
        context.pagination.page_count = 1;
        context.pagination.total = 0;
        if (!error.response || error.response.status != 404) {
          context.notify('Data Retrieved Failed', 'error');
        }
      }).onFinish(function() {
        if (seq == context.requestSeq) context.loading = false;
      }).call();
    },
    reload() {
      this.pagination.page = 1;
      this.get();
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.reload(), 400);
    },
    searchText(text) {
      this.search.q = text;
      this.reload();
    },
    setType(code) {
      this.search.project_type = code;
      this.reload();
    },
    setDue(value) {
      this.search.due = value;
      this.reload();
    },
    filter() {
      this.formFilter.show = true;
    },
    applyFilter() {
      this.formFilter.show = false;
      this.reload();
    },
    clearFilter(key) {
      this.search[key] = '';
      this.reload();
    },
    resetFilter() {
      this.search = { ...EMPTY_SEARCH };
      this.formFilter.show = false;
      this.reload();
    },
    exportUrl(path) {
      const keys = ['project_type', 'po_number', 'c_date', 'client', 'owner', 'site', 'kinds', 'due_date'];
      return this.apiUrl + path + '?' + keys.map(k => k + '=' + encodeURIComponent(this.search[k] || '')).join('&');
    },
    openDetail(row) {
      this.detail = { show: true, row };
    },
    editFromDetail() {
      const id = this.detail.row.id;
      this.detail.show = false;
      this.edit(id);
    },
    create() {
      this.form.title = "Add Data";
      this.workOrderData = { project_type: 'P', po_currency: 'IDR' };
      this.form.show = true;
    },
    edit(id) {
      let context = this;
      Api(context, workOrder.show(id)).onSuccess(function(response) {
        context.workOrderData = response.data.data;
        context.form.title = 'Edit Data';
        context.form.show = true;
      }).call();
    },
    save() {
      let context = this;
      const d = this.workOrderData;
      const required = ['project_type', 'po_number', 'c_date', 'client', 'owner', 'site', 'kinds', 'qty', 'weight', 'due_date'];
      if (required.some(k => d[k] === undefined || d[k] === null || String(d[k]).trim() === '')) {
        return alert('The Red Star Field is Mandatory');
      }

      let formData = new FormData();
      required.forEach(k => formData.append(k, d[k]));
      formData.append('project', d.project ? d.project : '-');
      // nilai PO opsional: tidak dikirim kalau kosong, backend menyimpannya sebagai NULL
      if (d.po_value !== undefined && d.po_value !== null && String(d.po_value).trim() !== '') {
        if (Number(d.po_value) < 0) return alert('Nilai PO tidak boleh negatif');
        formData.append('po_value', d.po_value);
      }
      formData.append('po_currency', d.po_currency || 'IDR');

      const isAdd = this.form.title == 'Add Data';
      const api = isAdd ? Api(context, workOrder.create(formData)) : Api(context, workOrder.update(d.id, formData));
      this.saving = true;
      api.onSuccess(function() {
        context.form.show = false;
        context.get();
        context.getStats();
        context.notify(isAdd ? 'Data Berhasil di Simpan' : 'Data Berhasil di Update', 'success');
      }).onError(function() {
        context.notify(isAdd ? 'Data Gagal di Simpan' : 'Data Gagal di Update', 'error');
      }).onFinish(function() {
        context.saving = false;
      }).call();
    },
    remove(row) {
      let warning = 'Hapus Work Order ' + row.wo_code + '?';
      if (row.pr_count) {
        warning += '\n\nPERHATIAN: nomor WO ini sudah dipakai di ' + row.pr_count + ' Purchase Request. PR tersebut tidak ikut terhapus, tetapi nomor WO-nya tidak lagi terdaftar.';
      }
      if (!confirm(warning)) return;
      let context = this;
      Api(context, workOrder.delete(row.id)).onSuccess(function() {
        context.get();
        context.getStats();
        context.notify('Data Successfully Deleted', 'success');
      }).call();
    },
    changePage(page) {
      this.pagination.page = page;
      this.get();
    },
    // ===== helper tampilan =====
    // kolom teks yang isinya cuma '-' atau '.' dianggap kosong
    has(value) {
      return value !== null && value !== undefined && !['', '-', '.'].includes(String(value).trim());
    },
    typeName(code) {
      const t = this.typeList.find(x => x.code == code);
      return t ? t.label : (code || '-');
    },
    qtyText(row) {
      if (!row.qty) return '-';
      return Number(row.qty).toLocaleString('id-ID') + ' ' + (row.weight || '');
    },
    money(value, currency) {
      return (currency || 'IDR') + ' ' + Number(value).toLocaleString('id-ID', { maximumFractionDigits: 2 });
    },
    poValueText(row) {
      return row.po_value !== null && row.po_value !== undefined && row.po_value !== '' ? this.money(row.po_value, row.po_currency) : '-';
    },
    fmtDate(date) {
      return date ? moment(date).locale('id').format('D MMM YYYY') : '-';
    },
    fmtDateTime(date) {
      return date ? moment(date).locale('id').format('D MMM YYYY HH:mm') : '-';
    },
    duration(row) {
      if (!row.c_date || !row.due_date) return null;
      return moment(row.due_date).diff(moment(row.c_date), 'days');
    },
    durationLabel(row) {
      const days = this.duration(row);
      if (days === null) return '';
      return days < 0 ? 'Due Date sebelum C Date, cek kembali tanggalnya' : 'Durasi ' + days + ' hari';
    },
    // % waktu yang sudah berjalan dari C Date ke Due Date (maks 100)
    timeProgress(row) {
      const total = this.duration(row);
      if (total === null) return 0;
      if (total <= 0) return 100;
      return Math.max(0, Math.min(100, moment(this.todayStr).diff(moment(row.c_date), 'days') / total * 100));
    },
    scheduleNote(row) {
      const total = this.duration(row);
      if (total === null) return '';
      const elapsed = moment(this.todayStr).diff(moment(row.c_date), 'days');
      if (elapsed < 0) return 'Belum dimulai, mulai ' + Math.abs(elapsed) + ' hari lagi';
      const due = this.dueOf(row);
      if (due.level == 'over') return 'Sudah melewati due date ' + due.label.replace('Lewat ', '');
      return 'Berjalan ' + elapsed + ' dari ' + total + ' hari (' + Math.round(this.timeProgress(row)) + '%)';
    },
    dueOf(row) {
      if (!row.due_date) return { level: 'none', label: '-' };
      const diff = moment(row.due_date).startOf('day').diff(moment(this.todayStr), 'days');
      if (diff < 0) return { level: 'over', label: 'Lewat ' + Math.abs(diff) + ' hari' };
      if (diff == 0) return { level: 'soon', label: 'Jatuh tempo hari ini' };
      if (diff <= DUE_SOON_DAYS) return { level: 'soon', label: diff + ' hari lagi' };
      return { level: 'ok', label: diff + ' hari lagi' };
    },
    notify(message, type) {
      const fn = type == 'success' ? toast.success : toast.error;
      fn(message, { position: toast.POSITION.BOTTOM_RIGHT, autoClose: 2000 });
    },
  }
};
</script>

<style scoped>
/* ===== modal ===== */
::v-deep .modal-container { display: flex; justify-content: center; align-items: center; overflow-y: auto; }
::v-deep .modal-content {
  display: flex; flex-direction: column; margin: 0 1rem; padding: 1rem;
  border: 1px solid #e2e8f0; border-radius: 0.75rem; background: #fff;
  width: 500px; margin-top: auto; margin-bottom: auto;
}
::v-deep .modal-form { width: 760px; max-width: 95vw; }
::v-deep .modal-detail { width: 680px; max-width: 95vw; padding: 1.25rem; }
.modal__title { font-size: 1.05rem; font-weight: 700; }
.section-title { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #8392ab; margin-bottom: 0.4rem; }
/* ===== hero ===== */
.hero {
  position: relative; overflow: hidden; border-radius: 20px; padding: 22px 26px 18px; color: #fff;
  background: radial-gradient(circle at 85% 10%, rgba(94, 114, 228, .55), transparent 45%),
              radial-gradient(circle at 10% 110%, rgba(17, 205, 239, .35), transparent 40%),
              linear-gradient(135deg, #141727 0%, #252a4a 60%, #1b2f4f 100%);
  box-shadow: 0 18px 40px rgba(20, 23, 39, .35);
}
.hero::after {
  content: ''; position: absolute; inset: 0; pointer-events: none; opacity: .07;
  background-image: repeating-linear-gradient(45deg, #fff 0 2px, transparent 2px 14px);
}
.hero-head { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 12px; position: relative; z-index: 1; }
.hero-kicker { font-size: .7rem; letter-spacing: .14em; text-transform: uppercase; color: #ffd600; font-weight: 700; }
.hero-title { color: #fff; font-weight: 800; margin: 2px 0 0; letter-spacing: -.02em; }
.hero-sub { font-size: .8rem; opacity: .7; }
.hero-actions { display: flex; gap: 8px; }
.hero-btn {
  display: inline-flex; align-items: center; gap: 6px; height: 38px; min-width: 38px; justify-content: center; padding: 0 12px;
  border-radius: 10px; border: 1px solid rgba(255,255,255,.18); background: rgba(255,255,255,.08); color: #fff; font-size: .8rem; font-weight: 600;
  transition: background .15s;
}
.hero-btn:hover { background: rgba(255,255,255,.18); color: #fff; }
.hero-btn-main { background: #ffd600; color: #1b1b1b; border-color: #ffd600; padding: 0 16px; }
.hero-btn-main:hover { background: #ffe24d; color: #1b1b1b; }
.hero-body { display: grid; grid-template-columns: 150px 170px 1fr 1fr; gap: 26px; align-items: center; margin-top: 18px; position: relative; z-index: 1; }
@media (max-width: 1200px) { .hero-body { grid-template-columns: 150px 1fr; } }
.donut-wrap { position: relative; width: 150px; height: 150px; }
.donut { width: 100%; height: 100%; transform: rotate(-90deg); }
.donut-track { fill: none; stroke: rgba(255,255,255,.1); stroke-width: 14; }
.donut-seg { fill: none; stroke-width: 14; transition: stroke-dasharray .6s ease; }
.donut-center { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.donut-num { font-size: 2.2rem; font-weight: 800; line-height: 1; }
.donut-lbl { font-size: .65rem; opacity: .7; text-transform: uppercase; letter-spacing: .08em; }
.donut-legend { display: flex; flex-direction: column; gap: 4px; }
.legend-row { display: flex; align-items: center; gap: 8px; padding: 5px 8px; border-radius: 8px; cursor: pointer; font-size: .78rem; }
.legend-row:hover, .legend-row.active { background: rgba(255,255,255,.1); }
.legend-dot { width: 10px; height: 10px; border-radius: 3px; }
.legend-name { flex: 1; opacity: .85; }
.legend-val { font-weight: 800; }
.legend-hint { font-size: .62rem; opacity: .45; padding-left: 8px; }
.hero-metrics { display: grid; grid-template-columns: 1fr; gap: 12px; }
.metric { border-left: 3px solid rgba(255,255,255,.18); padding-left: 12px; }
.metric-lbl { display: block; font-size: .68rem; text-transform: uppercase; letter-spacing: .06em; opacity: .65; }
.metric-lbl i { margin-right: 4px; }
.metric-val { display: block; font-size: 1.35rem; font-weight: 800; line-height: 1.25; }
.metric-val small { font-size: .8rem; opacity: .55; font-weight: 600; }
.metric-note { display: block; font-size: .68rem; opacity: .6; }
.metric-bar { height: 5px; border-radius: 3px; background: rgba(255,255,255,.12); margin: 4px 0 3px; max-width: 260px; overflow: hidden; }
.metric-bar div { height: 100%; background: linear-gradient(90deg, #2dce89, #2dcecc); border-radius: 3px; }
.text-danger-soft { color: #ff8fa3; }
.podium { background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.1); border-radius: 14px; padding: 12px 14px; }
.podium-title { display: block; font-size: .68rem; text-transform: uppercase; letter-spacing: .06em; opacity: .65; margin-bottom: 6px; }
.podium-row { display: flex; align-items: center; gap: 10px; padding: 5px 4px; border-radius: 8px; cursor: pointer; }
.podium-row:hover { background: rgba(255,255,255,.08); }
.podium-rank { width: 20px; height: 20px; border-radius: 6px; background: rgba(255,255,255,.12); font-size: .7rem; font-weight: 800; display: flex; align-items: center; justify-content: center; }
.podium-row:first-of-type .podium-rank { background: #ffd600; color: #1b1b1b; }
.podium-info { flex: 1; min-width: 0; }
.podium-code { font-size: .75rem; font-weight: 700; }
.podium-bar { height: 4px; border-radius: 2px; background: rgba(255,255,255,.1); margin-top: 3px; overflow: hidden; }
.podium-bar div { height: 100%; background: linear-gradient(90deg, #ffd600, #fb6340); }
.podium-val { font-size: .75rem; font-weight: 700; white-space: nowrap; }
.hero-warn { position: relative; z-index: 1; font-size: .68rem; color: #ffcf8a; margin: 12px 0 0; }

.type-mini { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; }
.type-mini span { font-size: .72rem; border-radius: 8px; padding: 3px 9px; cursor: pointer; background: rgba(255,255,255,.1); }
.type-mini span:hover { background: rgba(255,255,255,.2); }
.type-mini b { font-size: .9rem; margin-right: 2px; }
.tm-P b { color: #a9b4ff; }
.tm-I b { color: #7fe3f7; }
.tm-M b { color: #ffc08a; }
/* ===== tabel ===== */
.scroll { overflow: auto; max-height: 620px; }
.scroll thead th { position: sticky; top: 0; z-index: 3; background-color: #6495ED; }
.sticky-col { position: sticky; left: 0; background: #fff; z-index: 2; }
.scroll thead th.sticky-col { z-index: 4; background-color: #6495ED; }
tbody tr:hover .sticky-col { background: #f8f9fa; }
.avatar-initial { width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 13px; font-weight: 700; }
.avatar-lg { width: 52px; height: 52px; font-size: 18px; border-radius: 14px; }
.type-bg-P { background: linear-gradient(310deg, #5e72e4, #825ee4); }
.type-bg-I { background: linear-gradient(310deg, #1171ef, #11cdef); }
.type-bg-M { background: linear-gradient(310deg, #fb6340, #fbb140); }
.wo-cell { cursor: pointer; }
.wo-project { max-width: 230px; }
.cell-wide { max-width: 230px; white-space: normal; }
.time-bar { height: 6px; border-radius: 3px; background: #eef1f6; overflow: hidden; margin: 3px 0 4px; }
.time-bar div { height: 100%; border-radius: 3px; background: linear-gradient(90deg, #2dce89, #11cdef); }
.time-bar div.over { background: repeating-linear-gradient(45deg, #f5365c 0 6px, #ff8fa3 6px 12px); }
.time-bar-lg { height: 10px; border-radius: 5px; margin: 8px 0 6px; }
.due-badge { display: inline-block; font-size: 0.65rem; font-weight: 700; border-radius: 6px; padding: 2px 8px; }
.due-over { background: #fde1e6; color: #d6214a; }
.due-soon { background: #ffe8dc; color: #c2541f; }
.due-ok { background: #d8f7e9; color: #1a8f5c; }
.due-none { background: #e9ecef; color: #67748e; }
.action-icon { cursor: pointer; margin: 0 7px; }
.wo-link { cursor: pointer; color: #5e72e4; }
.wo-link:hover { text-decoration: underline; }
.po-currency { max-width: 95px; }
.filter-chip { cursor: pointer; }
/* ===== detail ===== */
.info-chip { display: inline-block; font-size: 0.7rem; font-weight: 600; color: #67748e; background: #f4f6fb; border-radius: 6px; padding: 2px 8px; margin-right: 4px; }
.info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
@media (max-width: 576px) { .info-grid { grid-template-columns: 1fr; } }
.info-cell { display: flex; gap: 10px; align-items: flex-start; background: #f8f9fe; border-radius: 10px; padding: 10px 14px; }
.info-cell i { color: #5e72e4; width: 16px; text-align: center; margin-top: 3px; }
.info-lbl { display: block; font-size: 0.65rem; text-transform: uppercase; font-weight: 700; color: #8392ab; letter-spacing: .03em; }
.info-val { display: block; font-size: 0.85rem; font-weight: 600; color: #344767; word-break: break-word; }
.schedule-box { border: 1px dashed #d6dcf5; border-radius: 12px; padding: 12px 16px; background: #fbfcff; }
.sched-date { display: block; font-size: 0.95rem; font-weight: 700; color: #344767; }
.audit { display: flex; flex-wrap: wrap; gap: 16px; font-size: 0.7rem; color: #8392ab; }
.audit i { margin-right: 4px; }
.dark-mode div::v-deep .modal-content { border-color: #2d3748; background-color: #1a202c; }
</style>
